import React, { useState, useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { User, UpdateUserData } from '@/types';

interface EditUserModalProps {
    onClose: () => void;
    onUpdate: (userData: UpdateUserData) => Promise<boolean>;
    user: User;
}

/**
 * Theme-aware modal component for editing existing users
 */
const EditUserModal: React.FC<EditUserModalProps> = ({ onClose, onUpdate, user }) => {
    const { isDark } = useTheme();
    const [formData, setFormData] = useState({
        username: user.username,
        email: user.email,
        password: '',
        confirmPassword: '',
        role: user.role,
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        team_code: ''
    });
    const [loading, setLoading] = useState(false);
    const [codeStatus, setCodeStatus] = useState<'idle' | 'loading' | 'valid' | 'invalid'>('idle');

    useEffect(() => {
        setFormData({
            username: user.username,
            email: user.email,
            password: '',
            confirmPassword: '',
            role: user.role,
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            team_code: ''
        });
    }, [user]);

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        if (!formData.username || !formData.email || !formData.first_name || !formData.last_name) {
            setError('Please fill in all required fields');
            return;
        }
        if (formData.password && (formData.password !== (formData as any).confirmPassword)) {
            setError('Passwords do not match');
            return;
        }

        try {
            setLoading(true);
            const ok = await onUpdate(formData);
            if (ok) {
                setSuccess('User updated successfully.');
                setTimeout(() => onClose(), 1200);
            } else {
                setError('Failed to update user. Please try again.');
            }
        } catch (error) {
            console.error('Error updating user:', error);
            setError(error instanceof Error ? error.message : 'An error occurred while updating the user. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="cgm-modal-backdrop">
            <div className="flex items-center justify-center min-h-screen p-4">
                <div className="cgm-card cgm-modal-panel p-8 max-w-md w-full">
                    <div className="text-center mb-6">
                                                 <h3 className="text-2xl cgm-text-primary mb-2">
                             Edit User
                         </h3>
                        <p className="cgm-text-secondary text-sm">
                            {user.full_name || user.username}
                        </p>
                    </div>
                    {error && (
                        <div className="cgm-alert-error px-4 py-3 rounded-md mb-4 text-sm">{error}</div>
                    )}
                    {success && (
                        <div className="cgm-alert-success px-4 py-3 rounded-md mb-4 text-sm">{success}</div>
                    )}
                    <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label htmlFor="firstName" className="block text-sm font-medium cgm-text-secondary mb-1">
                                    First Name
                                </label>
                                <input
                                    id="firstName"
                                    type="text"
                                    value={formData.first_name}
                                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                    disabled={loading}
                                    className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    placeholder="First name"
                                />
                            </div>
                            <div>
                                <label htmlFor="lastName" className="block text-sm font-medium cgm-text-secondary mb-1">
                                    Last Name
                                </label>
                                <input
                                    id="lastName"
                                    type="text"
                                    value={formData.last_name}
                                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                    disabled={loading}
                                    className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    placeholder="Last name"
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="username" className="block text-sm font-medium cgm-text-secondary mb-1">
                                Username
                            </label>
                            <input
                                id="username"
                                type="text"
                                required
                                value={formData.username}
                                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                disabled={loading}
                                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                placeholder="Enter username"
                            />
                        </div>

                        <div>
                            <label htmlFor="email" className="block text-sm font-medium cgm-text-secondary mb-1">
                                Email
                            </label>
                            <input
                                id="email"
                                type="email"
                                required
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                disabled={loading}
                                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                placeholder="Enter email"
                            />
                        </div>

                        

                        <div>
                            <label htmlFor="password" className="block text-sm font-medium cgm-text-secondary mb-1">
                                New Password (leave blank to keep current)
                            </label>
                            <input
                                id="password"
                                type="password"
                                name="new-password"
                                autoComplete="new-password"
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                disabled={loading}
                                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                placeholder="Enter new password (optional)"
                            />
                        </div>

                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium cgm-text-secondary mb-1">
                                Confirm New Password
                            </label>
                            <input
                                id="confirmPassword"
                                type="password"
                                name="confirm-new-password"
                                autoComplete="new-password"
                                value={formData.confirmPassword}
                                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                disabled={loading}
                                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                placeholder="Re-enter new password (optional)"
                            />
                        </div>

                        <div>
                            <label htmlFor="role" className="block text-sm font-medium cgm-text-secondary mb-1">
                                Role
                            </label>
                            <select
                                id="role"
                                value={formData.role}
                                onChange={(e) => setFormData({ ...formData, role: e.target.value as 'admin' | 'coach' | 'athlete' })}
                                disabled={loading || codeStatus === 'valid'}
                                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <option value="athlete">Athlete</option>
                                <option value="coach">Coach</option>
                                <option value="admin">Admin</option>
                            </select>
                        </div>

                        <div className="relative">
                            <label htmlFor="teamCode" className="block text-sm font-medium cgm-text-secondary mb-1">
                                Athlete/Coach Code (optional)
                            </label>
                            <input
                                id="teamCode"
                                type="text"
                                value={formData.team_code}
                                onChange={async (e) => {
                                    const value = e.target.value;
                                    setFormData({ ...formData, team_code: value });
                                    if (!value) { setCodeStatus('idle'); return; }
                                    setCodeStatus('loading');
                                    try {
                                        const token = localStorage.getItem('access');
                                        const res = await fetch(`/api/teams/admin/validate-team-code/?code=${encodeURIComponent(value)}`, {
                                            headers: token ? { 'Authorization': `Bearer ${token}` } : undefined
                                        });
                                        const data = await res.json();
                                        if (data.valid) {
                                            setCodeStatus('valid');
                                            // Force role to match detected
                                            if (data.role === 'coach' || data.role === 'athlete') {
                                                setFormData({ ...formData, team_code: value, role: data.role });
                                            }
                                        } else {
                                            setCodeStatus('invalid');
                                        }
                                    } catch (err) {
                                        setCodeStatus('invalid');
                                    }
                                }}
                                disabled={loading}
                                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                placeholder={'Enter join code (optional)'}
                            />
                            <div className="absolute right-3 top-11.5 -translate-y-1/2 flex items-center pointer-events-none">
                                {codeStatus === 'loading' && (
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-gray-500"></div>
                                )}
                                {codeStatus === 'valid' && (
                                    <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                                {codeStatus === 'invalid' && (
                                    <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                )}
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading || (formData.team_code ? codeStatus !== 'valid' : false)}
                            className="cgm-button w-full mt-6 py-3 px-4 flex items-center justify-center"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 mr-3 cgm-spinner border-2"></div>
                                    Updating User...
                                </>
                            ) : (
                                'Update User'
                            )}
                        </button>
                    </form>

                    <div className="mt-6 space-y-4 text-center">
                                                 <button
                             onClick={onClose}
                             className="cgm-cancel-button w-full py-3 px-4 flex items-center justify-center rounded-lg font-medium"
                         >
                             Cancel
                         </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditUserModal; 